import {
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InsightsIcon from '@mui/icons-material/Insights';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AdjustIcon from '@mui/icons-material/Adjust';
import { tokens } from '../../../../../global/theme/tokens';
import OverviewStatCard from './OverviewStatCard';
import ProgressDonut from './ProgressDonut';
import WorkPackageProgressRow from './WorkPackageProgressRow';
import { donePercent, normalizeTaskStats, sumTaskStats } from './overviewStats';

const VISIBLE_PACKAGES = 5;

const LegendDot = ({ color, label }) => (
  <Box className="flex items-center gap-1">
    <Box
      className="w-2 h-2 rounded-full shrink-0"
      sx={{ backgroundColor: color }}
    />
    <Typography fontSize="11px" className="opacity-60">
      {label}
    </Typography>
  </Box>
);

const MiniStat = ({ label, value, color }) => (
  <Box className="flex flex-col" sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        opacity: 0.55,
        fontSize: '0.66rem',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color, fontWeight: 700, fontSize: '0.95rem' }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Task counters of a project: the totals are the sum over the phases, the bars
 * show every work package. Collapsed only the numbers stay visible.
 */
const ProjectOverviewStats = ({ phases = [], defaultExpanded = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(defaultExpanded);

  // only running (0) and infinite (2) work packages count
  const packages = useMemo(
    () =>
      phases
        .flatMap((phase) => phase.workPackages || [])
        .filter((wp) => wp.status === 0 || wp.status === 2)
        .map(normalizeTaskStats),
    [phases],
  );

  const stats = useMemo(() => sumTaskStats(packages), [packages]);

  const percent = donePercent(stats);

  // long lists stay short until the user asks for the rest
  const [showAll, setShowAll] = useState(false);
  const visiblePackages = showAll
    ? packages
    : packages.slice(0, VISIBLE_PACKAGES);

  return (
    // same glass card as the invoice overview
    <Box
      sx={{
        background: `linear-gradient(135deg, ${colors.glassFrom}, ${colors.glassTo})`,
        border: `1px solid ${colors.selected}`,
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        padding: { xs: 1, md: 1.25 },
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 8px 24px -8px rgba(0,0,0,0.5)'
            : '0 8px 24px -8px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: isOpen ? 1.75 : 0,
        transition: 'gap 0.2s ease',
      }}
    >
      <Box
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 cursor-pointer"
        sx={{
          paddingX: 0.5,
          paddingY: 0.25,
          borderRadius: '8px',
          transition: 'background-color 0.15s ease',
          ':hover': { backgroundColor: `${colors.selected}40` },
        }}
      >
        <InsightsIcon sx={{ fontSize: '18px', color: colors.info }} />

        <Box className="flex flex-col sm:flex-row sm:items-baseline gap-x-2 grow min-w-0">
          <Typography fontSize="16px" fontWeight={600} noWrap>
            {t('Project overview')}
          </Typography>
          {isOpen && (
            <Typography fontSize="11px" noWrap className="opacity-60">
              {`${stats.total} ${t('Tasks')} ${t('in')} ${packages.length} ${t(
                'Work Packages',
              )}`}
            </Typography>
          )}
        </Box>

        {/* collapsed the numbers stay visible, so the row still says everything */}
        {!isOpen && (
          <Box className="flex items-center gap-4 sm:gap-6 shrink-0 pr-1">
            <MiniStat label={t('Open')} value={stats.open} />
            <MiniStat
              label={t('Overdue')}
              value={stats.overdue}
              color={stats.overdue ? colors.error : null}
            />
            <MiniStat label={t('Done')} value={stats.done} />
            <Box className="hidden sm:block">
              <MiniStat
                label={t('Points')}
                value={`${stats.doneScore}/${stats.totalScore}`}
              />
            </Box>
          </Box>
        )}

        <IconButton size="small" onClick={() => setIsOpen(!isOpen)}>
          <KeyboardArrowRightIcon
            sx={{
              transform: `rotate(${isOpen ? 90 : 0}deg)`,
              transition: 'transform 200ms',
            }}
          />
        </IconButton>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box className="flex flex-col gap-3">
          <Box
            sx={{
              display: 'grid',
              gap: 1.25,
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
            }}
          >
            <OverviewStatCard
              icon={<RadioButtonUncheckedIcon sx={{ fontSize: '15px' }} />}
              label={t('Open tasks')}
              value={stats.open}
              caption={t('in progress')}
              color={colors.info}
            />
            <OverviewStatCard
              icon={<ErrorOutlineIcon sx={{ fontSize: '15px' }} />}
              label={t('Deadline exceeded')}
              value={stats.overdue}
              caption={t('overdue')}
              color={colors.error}
            />
            <OverviewStatCard
              icon={<CheckCircleOutlineIcon sx={{ fontSize: '15px' }} />}
              label={t('Done')}
              value={stats.done}
              caption={`${t('of')} ${stats.total}`}
              color={colors.success}
            />
            <OverviewStatCard
              icon={<AdjustIcon sx={{ fontSize: '15px' }} />}
              label={t('Points')}
              value={stats.doneScore}
              caption={`${t('of')} ${stats.totalScore}`}
              color={colors.warning}
            />
          </Box>

          <Box
            className="flex flex-col lg:flex-row gap-3 items-center"
            sx={{ paddingTop: 1, borderTop: `1px solid ${colors.selected}` }}
          >
            {/* bars take three quarters, the donut the last one */}
            <Box
              className="flex flex-col gap-2 min-w-0"
              sx={{ width: { xs: '100%', lg: '75%' } }}
            >
              <Box className="flex flex-wrap items-center justify-between gap-2">
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.6,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  {t('Progress per work package')}
                </Typography>
                <Box className="flex items-center gap-3">
                  <LegendDot color={colors.success} label={t('Done')} />
                  <LegendDot color={colors.info} label={t('Open')} />
                  <LegendDot color={colors.error} label={t('Overdue')} />
                </Box>
              </Box>

              {visiblePackages.map((workPackage) => (
                <WorkPackageProgressRow
                  key={workPackage.id}
                  workPackage={workPackage}
                />
              ))}

              {packages.length > VISIBLE_PACKAGES && (
                <Button
                  size="small"
                  onClick={() => setShowAll(!showAll)}
                  sx={{ alignSelf: 'center', fontSize: '11px' }}
                >
                  {showAll
                    ? t('show less...')
                    : `${t('show more...')} (${
                        packages.length - VISIBLE_PACKAGES
                      })`}
                </Button>
              )}
            </Box>

            <Box
              className="flex justify-center lg:pl-4"
              sx={{
                width: { xs: '100%', lg: '25%' },
                borderLeft: { lg: `1px solid ${colors.selected}` },
              }}
            >
              <ProgressDonut stats={stats} percent={percent} />
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProjectOverviewStats;
